import React from 'react';
import { Link } from 'react-router-dom';
import { Image, Clock, CheckCircle, AlertCircle, Loader2, MoreVertical, Trash2, Sparkles, Pencil, Check, X } from 'lucide-react';
import { Listing } from '../types';
import { getSourceFileUrl, getThumbnailUrl, isBrowserViewableSource, supabase } from '../services/supabase';

interface ListingCardProps {
  listing: Listing;
  onDelete?: (id: string) => void;
  onRename?: (id: string, name: string) => void;
}

const TYPICAL_HDR_MS = 40 * 60 * 1000;

export function formatSentAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function estimatedProgress(createdAt: string, now: number): number {
  const started = Date.parse(createdAt);
  if (!Number.isFinite(started)) return 8;
  const elapsed = Math.max(0, now - started);
  return Math.min(90, Math.max(8, Math.round((elapsed / TYPICAL_HDR_MS) * 90)));
}

const ListingCard: React.FC<ListingCardProps> = ({ listing, onDelete, onRename }) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [nameValue, setNameValue] = React.useState(listing.name || listing.address || '');
  const [previewUrls, setPreviewUrls] = React.useState<string[]>([]);
  const [now, setNow] = React.useState(() => Date.now());
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  const isProcessing = listing.status === 'processing';

  React.useEffect(() => {
    if (!isProcessing) return;
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [isProcessing]);

  React.useEffect(() => {
    if (listing.cover_image_url || (listing.status !== 'processing' && listing.status !== 'pending')) {
      setPreviewUrls([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: media } = await supabase
        .from('hdr_media_items')
        .select('id')
        .eq('listing_id', listing.id)
        .order('sequence_number', { ascending: true })
        .limit(6);
      if (!media?.length || cancelled) return;
      const urls: string[] = [];
      for (const item of media) {
        const { data: sources } = await supabase
          .from('hdr_source_files')
          .select('storage_path')
          .eq('media_item_id', item.id)
          .order('exposure_value', { ascending: true, nullsFirst: false })
          .limit(1);
        const path = sources?.[0]?.storage_path;
        if (isBrowserViewableSource(path)) urls.push(getSourceFileUrl(path));
      }
      if (!cancelled) setPreviewUrls(urls);
    })().catch(() => {
      if (!cancelled) setPreviewUrls([]);
    });
    return () => {
      cancelled = true;
    };
  }, [listing.id, listing.cover_image_url, listing.status]);

  const startRename = () => {
    setNameValue(listing.name || listing.address || '');
    setIsRenaming(true);
  };

  const commitRename = () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== (listing.name || listing.address)) {
      onRename?.(listing.id, trimmed);
    }
    setIsRenaming(false);
  };

  const cancelRename = () => {
    setNameValue(listing.name || listing.address || '');
    setIsRenaming(false);
  };

  const statusConfig = {
    pending: {
      icon: Clock,
      color: 'text-slate-400',
      bg: 'bg-slate-700/80',
      label: 'Pending',
      borderColor: 'border-slate-600',
      animate: false,
      solid: false,
    },
    processing: {
      icon: Loader2,
      color: 'text-cyan-300',
      bg: 'bg-cyan-500/20',
      label: 'Processing',
      animate: true,
      borderColor: 'border-cyan-500/50',
      solid: false,
    },
    ready: {
      icon: CheckCircle,
      color: 'text-emerald-300',
      bg: 'bg-emerald-500/30',
      label: 'Ready',
      borderColor: 'border-emerald-500/50',
      animate: false,
      solid: true,
    },
    error: {
      icon: AlertCircle,
      color: 'text-red-400',
      bg: 'bg-red-500/20',
      label: 'Error',
      borderColor: 'border-red-500/50',
      animate: false,
      solid: false,
    },
  };

  const status = statusConfig[listing.status];
  const StatusIcon = status.icon;
  const progress = isProcessing ? estimatedProgress(listing.created_at, now) : 0;

  return (
    <div className={`group relative bg-slate-800/50 border rounded-xl overflow-hidden transition-all hover:shadow-lg ${
      isProcessing
        ? 'border-cyan-500/50 shadow-cyan-500/10 shadow-lg'
        : 'border-slate-700/50 hover:border-brand-500/50 hover:shadow-brand-500/10'
    }`}>
      <Link to={`/studio/${listing.id}`} className="block aspect-[4/3] relative">
        {listing.cover_image_url ? (
          <img
            src={getThumbnailUrl(listing.cover_image_url, { width: 600, height: 450, quality: 80 }) || listing.cover_image_url}
            alt={listing.address}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : previewUrls.length > 0 ? (
          <div className="w-full h-full relative bg-slate-900">
            <div className={`w-full h-full grid gap-0.5 ${previewUrls.length === 1 ? 'grid-cols-1' : previewUrls.length === 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'}`}>
              {previewUrls.slice(0, 4).map((url) => (
                <img
                  key={url}
                  src={getThumbnailUrl(url, { width: 400, height: 300, quality: 70 }) || url}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ))}
            </div>
            {isProcessing && (
              <div className="absolute inset-0 bg-slate-950/45 flex flex-col justify-end p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-cyan-100">Merging HDR…</p>
                  <p className="text-[10px] text-cyan-200/80">{progress}%</p>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-cyan-400 transition-[width] duration-700"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="mt-1.5 text-[10px] text-slate-300">Usually 20–40 minutes</p>
              </div>
            )}
          </div>
        ) : isProcessing ? (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 flex flex-col items-center justify-center relative overflow-hidden p-4">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/10 to-transparent animate-shimmer"
                 style={{ animationDuration: '2s', backgroundSize: '200% 100%' }} />

            <div className="relative">
              <div className="absolute inset-0 bg-cyan-500/20 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
              <div className="relative bg-slate-700/80 p-4 rounded-full border border-cyan-500/30">
                <Sparkles className="w-8 h-8 text-cyan-400 animate-pulse" />
              </div>
            </div>

            <p className="mt-4 text-sm text-cyan-300/80 font-medium">Merging HDR…</p>
            <div className="w-40 h-1.5 mt-3 rounded-full bg-white/10 overflow-hidden relative z-10">
              <div
                className="h-full rounded-full bg-cyan-400 transition-[width] duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-2 text-[10px] text-slate-400 relative z-10">Usually 20–40 minutes</p>
          </div>
        ) : (
          <div className="w-full h-full bg-slate-700 flex items-center justify-center">
            <Image className="w-12 h-12 text-slate-500" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

        <div className={`absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold border backdrop-blur-sm ${status.bg} ${status.color} ${status.borderColor}`}>
          <StatusIcon className={`w-3.5 h-3.5 ${status.animate ? 'animate-spin' : ''} ${status.solid ? 'fill-current' : ''}`} />
          {status.label}
        </div>

        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-slate-900/70 backdrop-blur-sm rounded-full text-xs text-slate-300 border border-slate-600/50">
          <Image className="w-3 h-3" />
          {listing.media_count}
        </div>

        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete?.(listing.id);
          }}
          className="absolute bottom-3 right-3 flex items-center justify-center w-8 h-8 bg-slate-900/70 hover:bg-red-500 text-slate-300 hover:text-white backdrop-blur-sm rounded-full border border-slate-600/50 hover:border-red-500 opacity-0 group-hover:opacity-100 transition-all"
          title="Delete listing"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </Link>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {isRenaming ? (
              <div className="flex items-center gap-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') cancelRename();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Project name"
                  className="flex-1 min-w-0 px-2 py-1 text-sm bg-slate-900 border border-brand-500 rounded-md text-white focus:outline-none"
                />
                <button
                  onClick={(e) => { e.stopPropagation(); commitRename(); }}
                  className="p-1 text-emerald-400 hover:bg-slate-700 rounded-md transition-colors"
                  title="Save"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); cancelRename(); }}
                  className="p-1 text-slate-400 hover:bg-slate-700 rounded-md transition-colors"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-semibold text-white truncate">
                  {listing.name || listing.address}
                </h3>
                {listing.name && (
                  <p className="text-sm text-slate-400 truncate mt-0.5">
                    {listing.address}
                  </p>
                )}
              </>
            )}
          </div>

          {!isRenaming && (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-40 bg-slate-700 border border-slate-600 rounded-lg shadow-xl z-50 py-1 animate-fade-in">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      startRename();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-200 hover:bg-slate-600 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Rename
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onDelete?.(listing.id);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Listing
                  </button>
                </div>
              </>
            )}
          </div>
          )}
        </div>

        <p className="text-xs text-slate-500 mt-2">
          Sent {formatSentAt(listing.created_at)}
        </p>
      </div>
    </div>
  );
};

export default ListingCard;
